/**
 * AQI — the Argo Quality Index.
 *
 * The north star says success is "measured by a single rising AQI score";
 * this module makes that score real. It is deterministic and fully
 * transparent: five 0–100 components, each computed from live store data
 * with a human-readable detail line, blended by fixed weights. No ML, no
 * magic — every point is traceable, which is what makes it a scoreboard
 * the team can actually move.
 *
 * Also home to `composeDailyBrief`, the agent-voiced morning summary the
 * Dashboard shows next to the dial (same inputs, same honesty rules).
 */

import type {
  Account,
  DeployedAgent,
  Person,
  ProjectTask,
  Ticket,
} from '../db/types';
import { getFollowUpReminders, type FollowUpReminder } from './reminders';
import type { ScanResult } from '../agent/systemScanner';

export interface AqiComponent {
  key: 'followups' | 'pipeline' | 'tickets' | 'tasks' | 'leverage';
  label: string;
  /** 0–100. */
  score: number;
  /** Relative weight in the blended score. */
  weight: number;
  /** Human-readable receipt, e.g. "2 of 9 follow-ups overdue". */
  detail: string;
}

export interface AqiResult {
  /** 0–100, weighted blend of the components. */
  score: number;
  components: AqiComponent[];
}

export interface AqiInput {
  people: Person[];
  accounts: Account[];
  tickets: Ticket[];
  tasks: ProjectTask[];
  agents: DeployedAgent[];
  /** Injectable clock for tests; defaults to now. */
  now?: number;
}

const DAY = 24 * 60 * 60 * 1000;

/** Actors that are agents, e.g. "claude:fixer", "argo". */
const AGENT_ACTOR = /^(claude|argo|agent)\b/i;

const pct = (good: number, total: number): number =>
  total === 0 ? 100 : Math.round((good / total) * 100);

export function computeAqi(input: AqiInput): AqiResult {
  const now = input.now ?? Date.now();
  const components: AqiComponent[] = [];

  // 1 — Follow-up discipline (25%). Overdue follow-ups are the clearest
  // signal of pipeline neglect; zero scheduled follow-ups is a free 100
  // only when there are no open prospects to neglect.
  {
    const all = getFollowUpReminders(input.people, 3650);
    const overdue = all.filter((r) => r.urgency === 'overdue').length;
    components.push({
      key: 'followups',
      label: 'Follow-up discipline',
      score: pct(all.length - overdue, all.length),
      weight: 25,
      detail:
        all.length === 0
          ? 'No follow-ups scheduled'
          : `${overdue} of ${all.length} follow-ups overdue`,
    });
  }

  // 2 — Pipeline freshness (25%). Share of open prospects touched in the
  // last 14 days. Deals don't die of rejection; they die of silence.
  {
    const open = input.people.filter(
      (p) =>
        p.category === 'prospect' &&
        p.stage !== 'closed-won' &&
        p.stage !== 'closed-lost',
    );
    const fresh = open.filter(
      (p) => p.lastTouch && now - Date.parse(p.lastTouch) <= 14 * DAY,
    ).length;
    components.push({
      key: 'pipeline',
      label: 'Pipeline freshness',
      score: pct(fresh, open.length),
      weight: 25,
      detail:
        open.length === 0
          ? 'No open prospects'
          : `${fresh} of ${open.length} open prospects touched ≤14d`,
    });
  }

  // 3 — Ticket flow (20%). Open tickets that are neither blocked nor past
  // deadline are healthy work-in-progress.
  {
    const open = input.tickets.filter(
      (t) => t.status !== 'done' && t.status !== 'cancelled',
    );
    const stuck = open.filter(
      (t) =>
        t.status === 'blocked' ||
        (t.deadline !== null && Date.parse(t.deadline) < now),
    ).length;
    components.push({
      key: 'tickets',
      label: 'Ticket flow',
      score: pct(open.length - stuck, open.length),
      weight: 20,
      detail:
        open.length === 0
          ? 'No open tickets'
          : `${stuck} of ${open.length} open tickets blocked or overdue`,
    });
  }

  // 4 — Task hygiene (15%). Open tasks past their due date drag the score.
  {
    const open = input.tasks.filter((t) => t.status !== 'done');
    const late = open.filter(
      (t) => t.dueDate && Date.parse(t.dueDate) < now,
    ).length;
    components.push({
      key: 'tasks',
      label: 'Task hygiene',
      score: pct(open.length - late, open.length),
      weight: 15,
      detail:
        open.length === 0
          ? 'No open tasks'
          : `${late} of ${open.length} open tasks past due`,
    });
  }

  // 5 — Agent leverage (15%). Share of the last 7 days' ticket + agent-run
  // events authored by agents, scaled so a 50% agent share scores 100.
  // This is the "compound output instead of headcount" needle.
  {
    let agentEvents = 0;
    let totalEvents = 0;
    const cutoff = now - 7 * DAY;
    for (const t of input.tickets) {
      for (const e of t.timeline) {
        if (Date.parse(e.at) < cutoff) continue;
        totalEvents++;
        if (AGENT_ACTOR.test(e.actor)) agentEvents++;
      }
    }
    for (const a of input.agents) {
      for (const e of a.timeline) {
        if (Date.parse(e.at) < cutoff) continue;
        totalEvents++;
        agentEvents++;
      }
    }
    const running = input.agents.filter((a) => a.state === 'running').length;
    const share = totalEvents === 0 ? 0 : agentEvents / totalEvents;
    components.push({
      key: 'leverage',
      label: 'Agent leverage',
      score: Math.min(100, Math.round(share * 200)),
      weight: 15,
      detail:
        totalEvents === 0
          ? `No activity in 7d · ${running} agent${running === 1 ? '' : 's'} running`
          : `${Math.round(share * 100)}% of 7d events by agents · ${running} running`,
    });
  }

  const weightSum = components.reduce((n, c) => n + c.weight, 0);
  const score = Math.round(
    components.reduce((n, c) => n + c.score * c.weight, 0) / weightSum,
  );
  return { score, components };
}

/** Tone band for the dial: what color/word the score deserves. */
export function aqiBand(score: number): 'strong' | 'steady' | 'slipping' {
  if (score >= 80) return 'strong';
  if (score >= 55) return 'steady';
  return 'slipping';
}

/**
 * The agent's morning brief: 2–4 plain sentences answering "what's my day?"
 * Deterministic and computed from the same live data as everything else —
 * it never pretends to be the hosted worker.
 */
export function composeDailyBrief(args: {
  reminders: FollowUpReminder[];
  scan: ScanResult;
  aqi: AqiResult;
}): string {
  const { reminders, scan, aqi } = args;
  const bits: string[] = [];

  const overdue = reminders.filter((r) => r.urgency === 'overdue');
  const today = reminders.filter((r) => r.urgency === 'today');

  // Headline: the day's load in one sentence.
  const load: string[] = [];
  if (overdue.length) load.push(`${overdue.length} follow-up${overdue.length === 1 ? '' : 's'} overdue`);
  if (today.length) load.push(`${today.length} due today`);
  if (scan.summary.high) load.push(`${scan.summary.high} high-severity finding${scan.summary.high === 1 ? '' : 's'}`);
  bits.push(
    load.length
      ? `On your plate: ${load.join(', ')}.`
      : 'Nothing overdue and no high-severity findings — clean slate.',
  );

  // The single suggested first move: most-overdue follow-up wins, else the
  // top scanner finding.
  if (overdue.length > 0) {
    const worst = overdue[0];
    bits.push(
      `Start with ${worst.person.name}${worst.person.company ? ` (${worst.person.company})` : ''} — ${Math.abs(worst.daysUntil)} day${Math.abs(worst.daysUntil) === 1 ? '' : 's'} overdue${worst.nextStep ? `: ${worst.nextStep}` : '.'}`,
    );
  } else if (scan.findings.length > 0) {
    bits.push(`Start with: ${scan.findings[0].title} (${scan.findings[0].detail}).`);
  }

  // The scoreboard, with the weakest component named so the score is a
  // lever rather than a vanity number.
  const weakest = [...aqi.components].sort((a, b) => a.score - b.score)[0];
  bits.push(
    `AQI is ${aqi.score} (${aqiBand(aqi.score)}); the biggest drag is ${weakest.label.toLowerCase()} — ${weakest.detail.toLowerCase()}.`,
  );

  return bits.join(' ');
}
